import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { ArrowLeft } from 'lucide-react'
import { Seo } from '@/seo/Seo'
import { breadcrumbSchema } from '@/seo/schemas'
import { articlesApi, type ArticleDetail as ArticleDetailType } from '@/api/articlesApi'
import { Button } from '@/components/ui/button'

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [article, setArticle] = useState<ArticleDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return

    let cancelled = false
    setLoading(true)
    setError(null)

    articlesApi
      .getBySlug(slug)
      .then((res) => {
        if (cancelled) return
        setArticle(res.data.article)
      })
      .catch(() => {
        if (cancelled) return
        setError('Article not found or failed to load.')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      {article && (
        <Seo
          title={`${article.title} | Recovry`}
          description={article.summary || `Read about ${article.title} on Recovry's clinical knowledge hub.`}
          canonical={`/knowledge/${slug}`}
          ogType="article"
          keywords={`${article.title}, physiotherapy, rehab, wellness, health tips, Recovry${article.tags?.length ? ', ' + article.tags.join(', ') : ''}`}
          schema={[
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Clinical Knowledge', path: '/knowledge' },
              { name: article.title, path: `/knowledge/${slug}` },
            ]),
            {
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.summary || '',
              url: `https://www.recovry.in/knowledge/${slug}`,
              datePublished: article.published_date || '',
              ...(article.updated_at ? { dateModified: article.updated_at } : {}),
              author: { '@type': 'Organization', name: 'Recovry' },
              publisher: {
                '@type': 'Organization',
                name: 'Recovry',
                logo: { '@type': 'ImageObject', url: 'https://www.recovry.in/newlogo.png' },
              },
              image: 'https://www.recovry.in/newlogo.png',
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://www.recovry.in/knowledge/${slug}`,
              },
            },
          ]}
        />
      )}
      <div className="mb-8">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/knowledge" className="inline-flex items-center gap-2">
            <ArrowLeft className="size-4" />
            Back to articles
          </Link>
        </Button>
      </div>

      {/* Loading Skeleton */}
      {loading && (
        <div className="space-y-4">
          <div className="h-8 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
          <div className="space-y-2 pt-4">
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
            <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      {/* Article - the stored HTML is self-contained (it carries its own title,
          category, date and body), so we render it directly. Adding a second
          title/date here would duplicate the header and create two <h1>s. */}
      {!loading && !error && article && (
        <article className="space-y-6">
          {/* Article Content (em dashes stripped per brand copy rules) */}
          <div
              className="
              article-content
              prose
              prose-lg
              max-w-none
              prose-headings:text-foreground
              prose-headings:font-semibold
              prose-h2:mt-12
              prose-h2:border-b
              prose-h2:pb-2
              prose-h3:mt-8
              prose-p:text-muted-foreground
              prose-ul:list-disc
              prose-ul:pl-6
              prose-li:my-1
              prose-hr:my-10
              prose-table:mt-8
              prose-table:border
              prose-table:border-gray-200
              prose-th:bg-muted
              prose-th:text-left
              prose-th:p-3
              prose-td:p-3
              prose-td:border
              prose-th:border
              [&_pre]:overflow-x-auto
              max-sm:[&_table]:block
              max-sm:[&_table]:overflow-x-auto
              "
              // Sanitize API-provided HTML before rendering \u2014 prevents stored XSS
              // (and token theft) if anything untrusted reaches the article store.
              // Articles are admin-authored in mongo and ship their own styling:
              // a leading <style> block plus class-based CSS and inline SVG art.
              // Two things are needed to keep that intact:
              //   - ADD_TAGS ['style'] so the <style> element survives, and
              //   - FORCE_BODY so it is parsed inside <body>. Without it the
              //     leading <style> lands in <head>, which DOMPurify drops
              //     wholesale (it only returns <body>) \u2014 that was stripping the CSS.
              // class/style attrs and SVG are allowed by default; scripts, event
              // handlers and javascript: URLs are still stripped.
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(article.content.replace(/\s*\u2014\s*/g, ', '), {
                  ADD_TAGS: ['style'],
                  FORCE_BODY: true,
                }),
              }}
            />
        </article>
      )}
    </div>
  )
}